import React, { useEffect, useRef, useState } from "react";
import { ArrowDown } from "lucide-react";
import { heroMedia, randomHeroIndex } from "../../data/impactStories";

const ROTATE_MS = 15000;

export default function RiHero() {
  const [idx, setIdx] = useState(randomHeroIndex);
  const wrapRef = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % heroMedia.length), ROTATE_MS);
    return () => clearInterval(t);
  }, []);

  // Hero compresses into the page on scroll
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const el = wrapRef.current;
        if (!el) return;
        const p = Math.min(window.scrollY / (window.innerHeight * 0.9), 1);
        el.style.transform = `scale(${1 - p * 0.06})`;
        el.style.borderRadius = `${p * 36}px`;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, []);

  return (
    <section data-testid="ri-hero" className="relative h-screen bg-[#08080a]">
      <div ref={wrapRef} className="absolute inset-0 overflow-hidden will-change-transform origin-center">
        {heroMedia.map((m, i) => (
          <div key={i} className={`absolute inset-0 transition-opacity duration-[2000ms] ${i === idx ? "opacity-100" : "opacity-0"}`}>
            {m.type === "video" ? (
              <video src={m.src} poster={m.poster} autoPlay muted loop playsInline className={`w-full h-full object-cover ${i === idx ? "hero-slow-zoom" : ""}`} />
            ) : (
              <img src={m.src} alt="" className={`w-full h-full object-cover ${i === idx ? "hero-slow-zoom" : ""}`} />
            )}
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-[#08080a]" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-transparent to-transparent" />

        <div className="relative z-10 h-full max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col justify-end pb-24">
          <div className="ri-fade-up" style={{ animationDelay: "0.2s" }}>
            <div className="inline-flex items-center gap-3 px-3 py-1.5 rounded-full border border-white/15 bg-black/30 backdrop-blur text-[11px] uppercase tracking-[0.25em] text-white/85 mb-7">
              <span className="w-1.5 h-1.5 rounded-full bg-[#F5D000] animate-pulse" />
              R&amp;C · Since 1957
            </div>
          </div>
          <h1 className="font-display text-white text-5xl sm:text-6xl lg:text-[5.75rem] font-bold leading-[0.98] max-w-4xl ri-fade-up" style={{ animationDelay: "0.45s" }}>
            Responsible
            <span className="block text-[#F5D000]">Infrastructure.</span>
          </h1>
          <p className="mt-6 text-white/75 text-base sm:text-lg leading-relaxed max-w-xl ri-fade-up" style={{ animationDelay: "0.7s" }}>
            How a 69-year-old family firm builds the bridges, highways and structures
            Britain relies on, responsibly, measurably, and in public.
          </p>
          <div className="mt-9 flex flex-wrap gap-4 ri-fade-up" style={{ animationDelay: "0.95s" }}>
            <a href="#journey" data-testid="ri-hero-explore-btn" className="btn-amber px-7 py-3.5 rounded-md text-sm font-semibold">
              Explore our journey
            </a>
            <a href="#wall" data-testid="ri-hero-stories-btn" className="btn-ghost px-7 py-3.5 rounded-md text-sm font-semibold">
              Latest Impact Stories
            </a>
          </div>

          <div className="mt-10 flex items-center gap-4 ri-fade-up" style={{ animationDelay: "1.2s" }}>
            <div className="flex gap-1.5" data-testid="ri-hero-dots">
              {heroMedia.map((_, i) => (
                <button
                  key={i}
                  aria-label={`Show ${heroMedia[i].label}`}
                  onClick={() => setIdx(i)}
                  className={`h-1 rounded-full transition-all duration-500 ${i === idx ? "w-8 bg-[#F5D000]" : "w-3 bg-white/25 hover:bg-white/50"}`}
                />
              ))}
            </div>
            <span className="text-[11px] uppercase tracking-[0.22em] text-white/45">{heroMedia[idx].label}</span>
          </div>
        </div>

        <a href="#journey" className="absolute bottom-7 right-8 z-10 hidden lg:flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/50 hover:text-[#F5D000] transition-colors">
          Scroll <ArrowDown className="w-3.5 h-3.5" />
        </a>
      </div>
    </section>
  );
}
